import React, { useContext, useMemo } from "react";
import ImageContainer from "@components/imageContainer";
import { resolvePath } from "@utils/resolvePath";
import {
  Container,
  CarouselTrack,
  CardWrapper,
  CarouselCard,
  CardContent,
  CornerBadge,
  CardTitle,
  CarouselCardTitle,
  CardDescription,
  CarouselCardAddress,
  RatingDistanceWrapper,
  BadgesContainer,
  Badge,
  EmptyState,
  EmptyStateText
} from "./styles";
import { CommonProps } from "@utils/interface";
import { convertToPascalCase } from "@utils/pascalCase";
import { connect } from "react-redux";
import { getWidgetSpecificIsVisible, getWidgetSpecificProps } from "@utils/index";
import { Context } from "@components/useContext/context";

interface AckoSelectGaragesProps extends CommonProps {
  garageList: unknown;
  styles?: React.CSSProperties;
  mStyles?: React.CSSProperties;
  emptyStateText?: string;
  emptyStateIllustration?: string;
}

type GarageRow = {
  id?: number | string;
  garage_vendor_id?: string;
  name?: string;
  network_type?: string;
  serviceable_make?: string[];
  city?: string;
  distance?: number | string;
  rating?: number | string | null;
  metadata?: {
    display_name?: string | null;
    legal_name?: string | null;
    display_address?: string | null;
    address?: string | null;
    city?: string | null;
    rating?: number | string | null;
    tags?: Array<string | { label?: string; name?: string }> | null;
    garage_type?: string | null;
    workshop_type?: string | null;
  };
  garage_type?: string | null;
};

function extractList(raw: unknown): GarageRow[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object" && raw !== null && "data" in raw) {
    const data = (raw as { data?: unknown }).data;
    return Array.isArray(data) ? (data as GarageRow[]) : [];
  }
  return [];
}

function isRowCarousel(styles: React.CSSProperties | undefined): boolean {
  if (!styles) return false;
  const styleMap = styles as Record<string, unknown>;
  return (
    styleMap.flexDirection === "row" ||
    styleMap.overflowX === "auto" ||
    styleMap.overflowX === "scroll"
  );
}

function isDealership(garage: GarageRow): boolean {
  return [
    garage.network_type,
    garage.garage_type,
    garage.metadata?.garage_type,
    garage.metadata?.workshop_type
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase())
    .some((value) => value.includes("dealer"));
}

function sortGarages(garages: GarageRow[]): GarageRow[] {
  return [...garages].sort((a, b) => {
    const dealershipDelta = Number(!isDealership(a)) - Number(!isDealership(b));
    if (dealershipDelta !== 0) return dealershipDelta;
    const aDistance = Number.isFinite(Number(a.distance)) ? Number(a.distance) : Number.MAX_SAFE_INTEGER;
    const bDistance = Number.isFinite(Number(b.distance)) ? Number(b.distance) : Number.MAX_SAFE_INTEGER;
    return aDistance - bDistance;
  });
}

/** Figma: title case (BFF/API often returns ALL CAPS). */
function formatGarageDisplayName(name: string) {
  const text = name.trim();
  if (!text) return "";
  return text
    .toLowerCase()
    .split(/\s+/)
    .map((word) =>
      word
        .split(/([-/:_.()[\]{}&]+)/)
        .map((part) => {
          if (!part || !/[a-zA-Z0-9]/.test(part)) return part;
          return part[0]!.toUpperCase() + part.slice(1);
        })
        .join("")
    )
    .join(" ");
}

function runClickActions(actions: CommonProps["actions"]) {
  actions?.on_click?.forEach((action) => {
    if (typeof action?.funCall === "function") {
      action.funCall({ ...action.args });
    }
  });
}

type ConfigurationState = {
  configuration?: {
    normalisedConfig?: Record<string, unknown>;
  };
};

const mapStateToProps = (state: ConfigurationState, ownProps: { id: string }) => {
  const cfg = state.configuration?.normalisedConfig;
  return {
    ...getWidgetSpecificProps(cfg, ownProps.id),
    is_visible: getWidgetSpecificIsVisible(cfg, ownProps.id)
  };
};

/** Stacked garage cards + empty state — same markup as network_garage_list_node (`acko_select_garages` stack layout). */
export type AckoGarageStackListViewProps = {
  garageList: unknown;
  emptyStateText?: string;
  emptyStateIllustration?: string;
  containerStyle?: React.CSSProperties;
  interactiveActions?: CommonProps["actions"];
  listTestId?: string;
};

export function AckoGarageStackListView(props: AckoGarageStackListViewProps) {
  const {
    garageList,
    emptyStateText = "No cashless garages found near you",
    emptyStateIllustration = "/images/garage.svg",
    containerStyle,
    interactiveActions,
    listTestId = "garage-list-embed",
  } = props;
  const garages = useMemo(() => sortGarages(extractList(garageList)), [garageList]);
  const hasClickAction = Boolean(interactiveActions?.on_click?.length);
  const handleCardClick = hasClickAction ? () => runClickActions(interactiveActions) : undefined;

  if (!garages.length) {
    return (
      <EmptyState data-testid="garage-empty-state-embed">
        {emptyStateIllustration ? (
          <ImageContainer
            imgSrc={emptyStateIllustration}
            alt="garage empty illustration"
            width={64}
            height={64}
            isResolvePath
          />
        ) : null}
        <EmptyStateText>{emptyStateText}</EmptyStateText>
      </EmptyState>
    );
  }

  return (
    <Container
      style={containerStyle}
      data-testid={listTestId}
      data-acko-garage-layout="stack"
    >
      {garages.map((garage, index) => (
        <Card
          key={garage?.id ?? garage?.garage_vendor_id ?? `${index}`}
          details={garage}
          isCarousel={false}
          onClick={handleCardClick}
        />
      ))}
    </Container>
  );
}

const AckoSelectGarages = (props: AckoSelectGaragesProps) => {
  const {
    garageList,
    styles,
    mStyles,
    is_visible,
    actions,
    emptyStateText = "No cashless garages found near you",
    emptyStateIllustration = "/images/garage.svg"
  } = props;
  const { isDesktop } = useContext(Context);
  const garages = useMemo(() => sortGarages(extractList(garageList)), [garageList]);
  const shouldRenderCarousel = isRowCarousel(styles);
  const hasClickAction = Boolean(actions?.on_click?.length);
  const handleCardClick = hasClickAction ? () => runClickActions(actions) : undefined;

  if (is_visible === false) return null;

  if (!garages.length) {
    return (
      <AckoGarageStackListView
        garageList={garageList}
        emptyStateText={emptyStateText}
        emptyStateIllustration={emptyStateIllustration}
      />
    );
  }

  if (shouldRenderCarousel) {
    const baseStyles = (styles || {}) as React.CSSProperties;
    const rowStyle: React.CSSProperties = {
      ...baseStyles,
      ...((!isDesktop && mStyles) || {})
    };

    return (
      <CarouselTrack
        style={rowStyle}
        data-testid="plans-garage-carousel"
        data-acko-garage-layout="row"
        data-acko-garage-mode={garages.length > 1 ? "carousel" : "single"}
        data-acko-garage-view={isDesktop ? "desktop" : "mweb"}
      >
        {garages.map((garage, index) => (
          <Card
            key={garage?.id ?? garage?.garage_vendor_id ?? `${index}`}
            details={garage}
            isCarousel
            onClick={handleCardClick}
          />
        ))}
      </CarouselTrack>
    );
  }

  return (
    <AckoGarageStackListView
      garageList={garageList}
      emptyStateText={emptyStateText}
      emptyStateIllustration={emptyStateIllustration}
      containerStyle={styles}
      interactiveActions={hasClickAction ? actions : undefined}
      listTestId="garage-list"
    />
  );
};

const Card = (props: { details: GarageRow; isCarousel: boolean; onClick?: () => void }) => {
  const { details, isCarousel, onClick } = props;
  const metadata = details?.metadata || {};
  const rating = details.rating ?? metadata.rating;
  const title = details.name || metadata.display_name || metadata.legal_name || "";
  const displayName = formatGarageDisplayName(String(title));
  const displayCity = metadata.city || details.city;
  const addressLine = metadata.display_address
    ? `${metadata.display_address}${displayCity ? `, ${convertToPascalCase(String(displayCity))}` : ""}`
    : metadata.address
      ? metadata.address
      : displayCity
        ? convertToPascalCase(String(displayCity))
        : "";
  const topTag = getTopTag(details);
  const garageType = isDealership(details) ? "dealership" : "network";
  const dataTestId = isCarousel ? "garage-carousel-card" : "garage-list-card";

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  if (isCarousel) {
    return (
      <CarouselCard
        data-testid={dataTestId}
        data-garage-type={garageType}
        data-distance={details.distance ?? ""}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        $interactive={Boolean(onClick)}
      >
        {topTag ? <CornerBadge role="text">{topTag}</CornerBadge> : null}
        <CardContent $insetForBadge={Boolean(topTag)}>
          {displayName ? <CarouselCardTitle>{displayName}</CarouselCardTitle> : null}
          {addressLine ? <CarouselCardAddress>{addressLine}</CarouselCardAddress> : null}
          <RatingAndDistance rating={rating} distance={details.distance} />
        </CardContent>
      </CarouselCard>
    );
  }

  return (
    <CardWrapper
      data-testid={dataTestId}
      data-garage-type={garageType}
      data-distance={details.distance ?? ""}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      $interactive={Boolean(onClick)}
    >
      <CardContent>
        <CardTitle>{title || displayName}</CardTitle>
        <RatingAndDistance rating={rating} distance={details.distance} />
        {addressLine ? <CardDescription>{addressLine}</CardDescription> : null}
        <BadgesContainer>
          <Badge>Live updates</Badge>
          <Badge>1-year warranty</Badge>
        </BadgesContainer>
      </CardContent>
    </CardWrapper>
  );
};

function RatingAndDistance(props: { rating?: number | string | null; distance?: number | string }) {
  const { rating, distance } = props;
  const parsedRating = Number(rating);
  const ratingText =
    rating != null && rating !== "" && Number.isFinite(parsedRating)
      ? `${parsedRating.toFixed(1)} rated`
      : "4.5 rated";
  const distanceText = distance != null && distance !== "" ? `${distance} km` : "";

  return (
    <RatingDistanceWrapper>
      <ImageContainer
        imgSrc={resolvePath("/images/full_star.svg")}
        alt="rating"
        width={12}
        height={12}
      />
      <span>{ratingText}</span>
      {distanceText ? <span>{` | ${distanceText}`}</span> : null}
    </RatingDistanceWrapper>
  );
}

function getTopTag(details: GarageRow): string | null {
  const rawTag = details.metadata?.tags?.[0];
  const tag =
    typeof rawTag === "string"
      ? rawTag
      : rawTag?.label || rawTag?.name || null;

  if (tag) return tag;
  if (isDealership(details) && details.serviceable_make?.[0]) {
    return `${details.serviceable_make[0]} Dealership`;
  }
  if (isDealership(details)) return "Brand Certified";
  return "ACKO CERTIFIED";
}

export default connect(mapStateToProps)(AckoSelectGarages);
